// client/src/coms/views/games_parts/GameRuntimeFrame.js
// Hosts the GameNest renderer in a sandboxed iframe (scripts only, no same-origin access) and relays
// this player's view in and their moves out.
import React, { useCallback, useEffect, useRef } from 'react';

export default function GameRuntimeFrame({ session, onMove, onRestart, onRuntimeError }) {
  const frameRef = useRef(null);
  const readyRef = useRef(false);
  const latestRef = useRef(session);
  latestRef.current = session;

  const sendRender = useCallback(() => {
    const frame = frameRef.current;
    const current = latestRef.current;
    if (!frame || !frame.contentWindow || !readyRef.current || !current || !current.state) return;
    frame.contentWindow.postMessage({
      type: 'gn:render',
      game: current.gameKey,
      seatIndex: current.seatIndex,
      state: current.state,
      players: current.seats.map((seat) => ({ name: seat.name, connected: seat.connected, isHost: seat.userId === current.hostUserId })),
    }, '*');
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      const frame = frameRef.current;
      if (!frame || event.source !== frame.contentWindow) return;
      const message = event.data;
      if (!message || typeof message !== 'object') return;
      if (message.type === 'gn:ready') {
        readyRef.current = true;
        sendRender();
      } else if (message.type === 'gn:move' && message.data && typeof message.data === 'object') {
        onMove(message.data);
      } else if (message.type === 'gn:restart') {
        if (onRestart) onRestart();
      } else if (message.type === 'gn:error' && onRuntimeError) {
        onRuntimeError(String(message.error || ''));
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onMove, onRestart, onRuntimeError, sendRender]);

  useEffect(() => {
    sendRender();
  }, [session, sendRender]);

  return (
    <iframe
      ref={frameRef}
      title={`${session.gameName} board`}
      data-testid="game-runtime-frame"
      src="/gamenest/runtime.html"
      sandbox="allow-scripts"
      style={{ width: '100%', height: '100%', minHeight: 420, border: 0, borderRadius: 12, background: '#f5f1ea', display: 'block' }}
    />
  );
}
